/**
 * Subunit-to-scene coordinate conversion utility
 *
 * Converts subunit position (parent-unit-local, feet) to scene-absolute feet.
 * Same math as obstacle-coordinates.ts but adapted for subunit data shape.
 */

import type { Subunit } from '@/types/subunit'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'

/**
 * Compute the scene-absolute position (in feet) for a subunit.
 *
 * Steps:
 * 1. Find the building containing the subunit's parent unit
 * 2. Apply unit offset with centering adjustment
 * 3. Rotate by building bearing
 * 4. Add building world position
 *
 * @returns { x, z } in scene-absolute feet, or null if building not found
 */
export function getSubunitScenePosition(
  subunit: Subunit,
  buildings: Building3DConfigExtended[]
): { x: number; z: number } | null {
  // Find building containing this subunit's parent unit
  let building: Building3DConfigExtended | undefined
  for (const b of buildings) {
    if (b.generatedUnits?.some((u) => u.id === subunit.parentUnitId)) {
      building = b
      break
    }
  }
  if (!building?.generatedUnits) return null

  // Find parent unit offsets
  const parentUnit = building.generatedUnits.find((u) => u.id === subunit.parentUnitId)
  if (!parentUnit) return null

  // Centering adjustment (same as SubunitRenderer / ObstacleRenderer)
  const centerX = building.width / 2
  const tpl = building.templateType as string | undefined
  const centerZ = (tpl === 'double_row' || tpl === 'nested_t_hangar') ? building.depth / 2 : 0
  const adjOffsetX = parentUnit.offset_x - centerX
  const adjOffsetY = parentUnit.offset_y - centerZ

  // Building-local position in feet (Y axis negated for scene Z)
  let localX = adjOffsetX + subunit.offsetX
  let localZ = -(adjOffsetY + subunit.offsetY)

  // Apply building rotation
  const buildingRotDeg = building.rotation ?? 0
  if (buildingRotDeg !== 0) {
    const rad = (buildingRotDeg * Math.PI) / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const rx = localX * cos + localZ * sin
    const rz = -localX * sin + localZ * cos
    localX = rx
    localZ = rz
  }

  // Add building world position to get scene-absolute feet
  return {
    x: building.position[0] + localX,
    z: building.position[2] + localZ,
  }
}
